<template>
    <div>
      <v-dialog v-model="isGalaxiesDialogOpen" max-width="800px">
        <v-card class="galaxy-dialog">
          <v-card-title class="headline">
            <v-icon left color="#004bfb">mdi-creation</v-icon>
            Galaxies in Universe "{{ currentUniverseName }}"
            <v-spacer></v-spacer>
            <v-btn icon @click="isGalaxiesDialogOpen = false" dark>
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="#004bfb"
              @click="openCreateGalaxyDialog"
              class="px-4 py-2 mt-2"
            >
              <v-icon class="white--text" left>mdi-plus</v-icon>
              <span class="white--text">
              Create new galaxy
              </span>
            </v-btn>
          </v-card-actions>
          <v-card-text class="pt-4">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  text
                  color="#004bfb"
                  v-bind="attrs"
                  v-on="on"
                  class="mb-4"
                >
                  <v-icon left>mdi-information-outline</v-icon>
                  About Galaxies
                </v-btn>
              </template>
              <v-card class="pa-4" max-width="350">
                <h3 class="headline mb-2">Galaxies: Topic Clusters</h3>
                <v-divider class="mb-3"></v-divider>
                <p class="subtitle-1 mb-2">Galaxies are the second level in our cosmic data hierarchy:</p>
                <v-chip color="#004bfb" small class="mb-3 white--text">Universe > Galaxy > Planet > Moon > Object</v-chip>
                <p class="body-2 mb-3">
                  <strong>Key Features:</strong>
                </p>
                <ul class="body-2 pl-4 mb-3">
                  <li><strong>Topic Categories:</strong> Each Galaxy represents a major knowledge area</li>
                  <li><strong>Multiple Planets:</strong> Galaxies contain numerous Planets for specific sub-topics</li>
                  <li><strong>Conversation Organization:</strong> Group related discussions and content</li>
                </ul>
                <v-divider class="mb-3"></v-divider>
                <p class="body-2 mb-2"><strong>When you select a Galaxy:</strong></p>
                <ul class="body-2 pl-4">
                  <li>All conversations, searches, and uploads will be associated with it</li>
                  <li>New planets created will belong to the selected Galaxy</li>
                  <li>Your context will be focused on the Galaxy's knowledge domain</li>
                </ul>
                <v-divider class="my-3"></v-divider>
                <p class="caption font-italic">
                  Navigate between Galaxies to switch between different knowledge domains and conversation spaces!
                </p>
              </v-card>
            </v-tooltip>
            <v-text-field
              v-model="galaxySearch"
              label="Search galaxies"
              prepend-inner-icon="mdi-magnify"
              clearable
              outlined
              dense
            ></v-text-field>
            <v-list>
              <v-list-item
                v-for="galaxy in filteredGalaxies"
                :key="galaxy.id"
                @click="selectGalaxy(galaxy.id)"
                :class="{'selected-galaxy': selectedGalaxy === galaxy.id}"
              >
                <v-list-item-avatar>
                  <v-icon :color="galaxy.color || getRandomColor()">mdi-creation</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ galaxy.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ galaxy.description || 'No description available' }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    small
                    color="#004bfb"
                    outlined
                    @click.stop="selectGalaxy(galaxy.id)"
                  >
                    Select
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-dialog>
    </div>
  </template>

  <script>
  export default {
    name: 'GalaxyDialog',
    props: {
      value: {
        type: Boolean,
        default: false
      },
      galaxies: {
        type: Array,
        default: () => []
      },
      selectedGalaxy: {
        type: [String, Number],
        default: null
      },
      currentUniverseName: {
        type: String,
        default: 'Universe'
      }
    },
    data() {
      return {
        galaxySearch: '',
      }
    },
    computed: {
      isGalaxiesDialogOpen: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        }
      },
      filteredGalaxies() {
        if (!this.galaxySearch) {
          return this.galaxies;
        }

        const search = this.galaxySearch.toLowerCase();
        const filtered = this.galaxies.filter(galaxy =>
          galaxy.name.toLowerCase().includes(search) ||
          (galaxy.description && galaxy.description.toLowerCase().includes(search))
        );
        return filtered;
      },
    },
    methods: {
      selectGalaxy(id) {
        this.$emit('galaxy-selected', id);
        this.isGalaxiesDialogOpen = false;
      },
      openCreateGalaxyDialog() {
        this.$emit('open-create-galaxy-dialog');
        this.isGalaxiesDialogOpen = false;
      },
      getRandomColor() {
        const colors = [
          'indigo darken-2',
          'deep-purple darken-2',
          'blue darken-3',
          'teal darken-2',
          'green darken-3',
          'orange darken-3',
          'deep-orange darken-2',
          'brown darken-2',
          'blue-grey darken-2',
          'red darken-2',
          'pink darken-2'
        ];
        return colors[Math.floor(Math.random() * colors.length)];
      }
    }
  }
  </script>

  <style scoped>
  .galaxy-dialog .v-card__title {
    background: linear-gradient(to right, rgba(0, 75, 251, 0.1) 0%, rgba(0, 75, 251, 0.3) 50%, rgba(0, 75, 251, 0.1) 100%);
    padding: 16px;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  .selected-galaxy {
    background: rgba(0, 75, 251, 0.1);
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 75, 251, 0.2);
    transition: all 0.3s ease;
  }

  .v-list-item {
    transition: all 0.3s ease;
    border-radius: 8px;
    margin-bottom: 4px;
  }

  .v-list-item:hover {
    background: rgba(0, 75, 251, 0.05);
    transform: translateY(-2px);
  }

  .theme--dark .galaxy-dialog .v-card__title {
    background: linear-gradient(to right, rgba(0, 75, 251, 0.2) 0%, rgba(0, 75, 251, 0.4) 50%, rgba(0, 75, 251, 0.2) 100%);
  }

  .theme--dark .selected-galaxy {
    background: rgba(0, 75, 251, 0.3);
  }

  .theme--dark .v-list-item:hover {
    background: rgba(0, 75, 251, 0.15);
  }
  </style>
